import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import users from "../../apiConfigs/users";
import { ReactComponent as Add } from "../../assets/add.svg";
import { ReactComponent as SearchSVG } from "../../assets/icons/search.svg";
import { ReactComponent as View } from "../../assets/view.svg";
import Pagination from "../../components/Pagination";
import Button from "../../designComponents/Button";
import Datepicker from "../../designComponents/Datepicker";
import DeleteModal from "../../designComponents/DeleteModal";
import Dropdown from "../../designComponents/Dropdown/Dropdown";
import ReusableModalButton from "../../designComponents/FilterButton";
import Input from "../../designComponents/Input";
import Table from "../../designComponents/Table";
import {
  renderFullDetails,
  renderRole,
} from "../../designComponents/Table/renderMethods";
import { ADD_USER_ROUTE } from "../../routes/routePaths";
import { humanReadableDate } from "../../utils/date";
import { debounce } from "../../utils/debounce";
import requestAPI from "../../utils/requestAPI";
import { ButtonsSection, Title, Wrapper } from "../Properties/Details/styles";
import { SearchInputWrapperStyled } from "./style";

type Props = {};
export type UserTypes = {
  _id: string;
  firstName: string;
  lastName: string;
  isDeveloper?: boolean;
  avatar: string;
  email: string;
  status: string;
  phone: string;
  roles: string[];
  address: string;
  onboardingStep: number;
  createdAt: string;
};
interface FilterValues {
  location: string;
  from: string | null | Date;
  to: string | null | Date;
  roles: string;
  status: string;
  page?: number;
  limit?: number;
  search?: string;
}
const Users = (props: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [usersData, setUsersData] = useState<UserTypes[]>([]);
  const [showDevs, setShowDevs] = useState<boolean>(false);
  const [ids, setIds] = useState<string[]>([]);
  const [paginationParams, setPaginationParams] = useState<FilterValues>({
    page: 1,
    limit: 10,
    search: "",
    location: "",
    from: "",
    to: "",
    roles: params.get("role") ?? "",
    status: "",
  });
  const [filterValues, setFilterValues] = useState<FilterValues>({
    location: "",
    from: "",
    to: "",
    roles: "",
    status: "",
  });
  const [filteredUsers, setFilteredUsers] = useState<UserTypes[]>([]);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    total: 0,
    currentPage: 1,
  });

  const getAllUsers = async (params: any) => {
    setLoading(true);
    try {
      const query = {
        ...params,
      };
      const response = await requestAPI(users.getUsers(query));
      setUsersData(response.data);
      setPagination({
        totalPages: response?.pagination?.totalPages,
        total: response?.pagination?.total,
        currentPage: response?.pagination?.currentPage,
      });
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const delayedFetch = useCallback(debounce(getAllUsers, 300), [
    paginationParams,
  ]);
  useEffect(() => {
    if (showDevs) {
      setFilteredUsers(usersData.filter((user) => user.isDeveloper));
    } else {
      setFilteredUsers(usersData);
    }
  }, [showDevs, usersData]);
  useEffect(() => {
    delayedFetch(paginationParams);
    setShowDevs(false);
  }, [paginationParams, delayedFetch]);

  const tableData = {
    headings: [
      { key: "fullName", label: "Full Detail" },
      { key: "role", label: "Role" },
      { key: "address", label: "Address" },
      { key: "ob", label: "On Boarding" },
      { key: "date", label: "Date Registration" },
      { key: "phone", label: "Phone Number" },
      { key: "view", label: "View" },
    ],
    body: Array.isArray(usersData)
      ? (showDevs ? filteredUsers : usersData)?.map((user) => ({
          id: user?._id,
          fullName: renderFullDetails({
            avatar: user.avatar,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
          }),
          role: renderRole(user.roles),
          ob:
            user.onboardingStep === 4
              ? "Complete"
              : `InComplete (${user.onboardingStep}/4)`,
          address: user.address,
          phone: user.phone,
          date: humanReadableDate(user.createdAt),
          view: (
            <div
              onClick={() => {
                navigate(user?._id);
              }}
              style={{ cursor: "pointer" }}
            >
              <View fill="#7E7E7E"></View>
            </div>
          ),
        }))
      : [],
  };
  const [changeCount, setChangeCount] = useState(0);

  useEffect(() => {
    const fieldsToTrack = ["status", "from", "to", "roles"];
    let changes = 0;

    fieldsToTrack.forEach((field) => {
      //@ts-ignore
      if (paginationParams[field] !== "") {
        changes++;
      }
    });

    setChangeCount(changes);
  }, [paginationParams]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPaginationParams((prevParams) => {
      const updatedParams = { ...prevParams, search: value, page: 1 };
      delayedFetch(updatedParams);
      return updatedParams;
    });
  };

  const handleCurrentPage = (page: number) => {
    setPaginationParams((prevParams) => ({ ...prevParams, page }));
  };

  const handleLimitChange = (limit: number) => {
    setPaginationParams((prevParams) => ({ ...prevParams, limit, page: 1 }));
  };

  return (
    <Wrapper>
      <ButtonsSection>
        <Title>User Management</Title>
        <Button
          icon={<Add fill="#FFFFFF" height={10} width={10}></Add>}
          style={{
            backgroundColor: "#0A56AF",
            borderRadius: 8,
            fontSize: 12,
          }}
          onClick={() => {
            navigate(ADD_USER_ROUTE);
          }}
        >
          Add Users
        </Button>
      </ButtonsSection>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <SearchInputWrapperStyled style={{ width: 367 }}>
          <Input
            value={paginationParams.search}
            name="users"
            onChange={handleInputChange}
            placeholder="Search Users"
            iconLeft={<SearchSVG />}
          />
        </SearchInputWrapperStyled>
        <div style={{ display: "flex", gap: 16 }}>
          <ReusableModalButton icon changeCount={changeCount}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div>
                {/* <LabelInputField
                  placeHolder="Eg. 4 Oakwood Avenue, Waterford"
                  value={filterValues.location}
                  onChange={(e) => {
                    setFilterValues({
                      ...filterValues,
                      location: e.target.value,
                    });
                  }}
                  name="search"
                  label="Search Location"
                  style={{
                    label: {
                      fontSize: "14px",
                      fontWeight: "500px",
                      color: "#555A77",
                    },
                    input: {
                      width: "100%",
                      height: 50,
                      border: "1px solid #F1EFFB",
                      padding: 8,
                      borderRadius: 6,
                    },
                    container: {
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      paddingTop: 14,
                    },
                  }}
                ></LabelInputField> */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                  }}
                >
                  <Datepicker
                    selectedDate={filterValues.from}
                    label="Date Filter"
                    name="from"
                    onChange={(date) => {
                      setFilterValues({
                        ...filterValues,
                        from: date,
                      });
                    }}
                    style={{
                      label: {
                        fontSize: "14px",
                        fontWeight: "500px",
                        color: "#555A77",
                      },
                      input: {
                        height: 50,
                        border: "1px solid #F1EFFB",
                        padding: 8,
                        borderRadius: 6,
                        width: "100%", // This makes the input take 100% of its container
                      },
                      container: {
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        paddingTop: 14,
                        width: "50%", // This makes the container take up 50% of the parent's width
                      },
                    }}
                  />

                  <Datepicker
                    selectedDate={filterValues.to}
                    label=" "
                    name="to"
                    onChange={(date) => {
                      setFilterValues({
                        ...filterValues,
                        to: date,
                      });
                    }}
                    style={{
                      label: {
                        fontSize: "14px",
                        fontWeight: "500px",
                        color: "#555A77",
                      },
                      input: {
                        height: 50,
                        border: "1px solid #F1EFFB",
                        padding: 8,
                        borderRadius: 6,
                        width: "100%", // This makes the input take 100% of its container
                      },
                      container: {
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        paddingTop: 34,
                        width: "50%", // This makes the container take up 50% of the parent's width
                      },
                    }}
                  />
                </div>

                <Dropdown
                  value={filterValues.roles}
                  label="Role"
                  onChange={(e) => {
                    setFilterValues({
                      ...filterValues,
                      roles: e.target.value,
                    });
                  }}
                  options={[
                    { label: "All", value: "" },
                    { label: "Tenant", value: "TENANT" },
                    { label: "Landlord", value: "LANDLORD" },
                    { label: "Service Provider", value: "SERVICE_PROVIDER" },
                  ]}
                  style={{
                    container: { paddingTop: 10 },
                    label: { paddingBottom: 6, color: "#555A77" },
                    select: {
                      backgroundColor: "#F1EFFB",
                      fontSize: 14,
                      fontFamily: "urbanist",
                      fontWeight: 500,
                      padding: "8px",
                    },
                  }}
                ></Dropdown>
                <Dropdown
                  label="Status"
                  value={filterValues.status}
                  onChange={(e) => {
                    setFilterValues({
                      ...filterValues,
                      status: e.target.value,
                    });
                  }}
                  options={[
                    { label: "All", value: "" },
                    { label: "Active", value: "ACTIVE" },
                    { label: "Inactive", value: "INACTIVE" },
                    { label: "Suspended", value: "SUSPENDED" },
                    { label: "Blocked", value: "BLOCKED" },
                  ]}
                  style={{
                    container: { paddingTop: 10 },
                    label: { paddingBottom: 6, color: "#555A77" },
                    select: {
                      backgroundColor: "#F1EFFB",
                      fontSize: 14,
                      fontFamily: "urbanist",
                      fontWeight: 500,
                      padding: "8px",
                    },
                  }}
                ></Dropdown>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 10,
                  }}
                >
                  <Button
                    style={{
                      borderRadius: 6,
                      color: "#7E7E7E",
                      backgroundColor: "white",
                      border: "1px solid #D8D8D8",
                    }}
                    onClick={() => {
                      setFilterValues({
                        location: "",
                        from: "",
                        to: "",
                        roles: "",
                        status: "",
                      });
                    }}
                  >
                    {" "}
                    Reset Filter
                  </Button>
                  <Button
                    onClick={() => {
                      setPaginationParams({
                        ...paginationParams,
                        location: filterValues.location,
                        roles: filterValues.roles,
                        status: filterValues.status,
                        from: filterValues.from,
                        to: filterValues.to,
                      });
                    }}
                    style={{
                      borderRadius: 6,
                      backgroundColor: "#7E7E7E",
                    }}
                  >
                    {" "}
                    Set Filter{" "}
                  </Button>
                </div>
              </div>
            </div>
          </ReusableModalButton>

          {ids.length > 0 && (
            <Button
              style={{
                backgroundColor: "#f8dcec",
                color: "#CF69AB",
                borderRadius: 6,
              }}
              onClick={() => {
                setOpenDeleteModal(true);
              }}
            >
              {" "}
              Remove users
            </Button>
          )}
        </div>
      </div>

      {openDeleteModal && (
        <DeleteModal
          title="Remove Users"
          loading={false}
          onDelete={() => {}}
          isOpen={true}
          onClose={() => {
            setOpenDeleteModal(false);
          }}
          itemName="all the selected users?"
        ></DeleteModal>
      )}
      <Table
        showCheckBox={false}
        checkedData={ids}
        tableData={tableData}
        isLoading={loading}
        noData="No data to display."
        showMenuIcon={false}
        evenRowBgColor="#fff"
        headingBgColor="#fdfdf9"
        onCheck={(data) => {
          setIds(data);
        }}
      />

      <Pagination
        activePage={pagination.currentPage}
        totalPages={pagination.totalPages}
        limit={paginationParams.limit}
        onCurrentPageChange={handleCurrentPage}
        onLimitChange={handleLimitChange}
      />
    </Wrapper>
  );
};

export default Users;
